"use strict";
/*
 * Copyright Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.isVectorBasemapShortname = exports.DEFAULT_FALLBACK_BASEMAP_SHORTNAME = exports.DEFAULT_MAX_BASEMAP_ZOOM = exports.OSM_DEFAULT_LAYER = void 0;
exports.composeMapStyleWithOverlay = composeMapStyleWithOverlay;
exports.getProjectMapBaseLayers = getProjectMapBaseLayers;
exports.isBuiltInOsm = isBuiltInOsm;
exports.getProjectBasemapByShortname = getProjectBasemapByShortname;
exports.getBuiltInOsmRasterIfActive = getBuiltInOsmRasterIfActive;
exports.getBasemapZoomBoundsFromConfigFields = getBasemapZoomBoundsFromConfigFields;
exports.getZoomBoundsForBasemapShortname = getZoomBoundsForBasemapShortname;
exports.getZoomBoundsForOsMFallbackBasemap = getZoomBoundsForOsMFallbackBasemap;
exports.getBasemapZoomHintMessage = getBasemapZoomHintMessage;
exports.isStyleBasemapShortname = isStyleBasemapShortname;
exports.getValidBasemapShortnames = getValidBasemapShortnames;
exports.getMaxConfiguredRasterBasemapZoom = getMaxConfiguredRasterBasemapZoom;
exports.formatBasemapDisplayName = formatBasemapDisplayName;
exports.formatRasterBasemapAttribution = formatRasterBasemapAttribution;
const cloneDeep_1 = __importDefault(require("lodash/cloneDeep"));
const mapBaseLayersProject_types_1 = require("chaire-lib-common/lib/config/mapBaseLayersProject.types");
const project_config_1 = __importDefault(require("chaire-lib-frontend/lib/config/project.config"));
const layers_config_1 = require("./layers.config");
/**
 * Copy layer from bundled config (already includes `process.env` expanded when webpack built `__CONFIG__`).
 * Deep-clone inline `style` so overlay composition does not mutate the shared config object.
 */
function resolveProjectMapBasemapLayer(layer) {
    if ((0, mapBaseLayersProject_types_1.isProjectBasemapStyleUrl)(layer)) {
        return Object.assign({}, layer);
    }
    if ('style' in layer && layer.style) {
        return Object.assign(Object.assign({}, layer), { style: (0, cloneDeep_1.default)(layer.style) });
    }
    throw new Error(`[mapBaseLayers] "${layer.shortname}" must include a MapLibre style: set "styleUrl" or inline "style".`);
}
/** Clones a MapLibre style and appends the Transition dimming overlay so it covers labels/symbols too. */
function composeMapStyleWithOverlay(baseStyle, overlayOpacityPct, overlayColor) {
    const style = (0, cloneDeep_1.default)(baseStyle);
    style.version = 8;
    style.sources = style.sources || {};
    style.layers = style.layers || [];
    style.sources.overlay = {
        type: 'geojson',
        data: layers_config_1.overlaySource
    };
    const overlayLayer = {
        id: 'base-overlay',
        type: 'fill',
        source: 'overlay',
        paint: {
            'fill-color': overlayColor === 'white' ? '#ffffff' : '#000000',
            'fill-opacity': overlayOpacityPct / 100
        }
    };
    const existingIdx = style.layers.findIndex((l) => l.id === 'base-overlay');
    if (existingIdx >= 0) {
        style.layers.splice(existingIdx, 1);
    }
    style.layers.push(overlayLayer);
    return style;
}
/** Built-in OSM; labels from i18n in the app (not from this object). */
exports.OSM_DEFAULT_LAYER = {
    type: 'raster',
    shortname: 'osm',
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'
};
let _cachedLayers = null;
/**
 * OSM plus project `mapBaseLayers` from bundled config (secrets come from `process.env` in `config.js` at webpack build).
 * Result is computed once and cached since the config is static at runtime.
 */
function getProjectMapBaseLayers() {
    if (_cachedLayers) {
        return _cachedLayers;
    }
    const raw = project_config_1.default.mapBaseLayers || [];
    const extra = raw.map((ly) => resolveProjectMapBasemapLayer(ly));
    const seen = new Set([exports.OSM_DEFAULT_LAYER.shortname]);
    for (const layer of extra) {
        if (seen.has(layer.shortname)) {
            console.warn(`[mapBaseLayers] Duplicate shortname "${layer.shortname}" — only the first entry will be used.`);
        }
        seen.add(layer.shortname);
    }
    _cachedLayers = [exports.OSM_DEFAULT_LAYER, ...extra];
    return _cachedLayers;
}
/** True for the built-in OSM entry (no `name`; project entries always have `name`). */
function isBuiltInOsm(layer) {
    return layer.shortname === 'osm' && !('name' in layer);
}
function getProjectBasemapByShortname(shortname) {
    const layer = getProjectMapBaseLayers().find((l) => l.shortname === shortname);
    if (!layer || isBuiltInOsm(layer)) {
        return undefined;
    }
    return layer;
}
/** Built-in OSM raster only (for the legacy raster + overlay initial style path). */
function getBuiltInOsmRasterIfActive(shortname) {
    if (shortname !== 'osm') {
        return undefined;
    }
    const layer = getProjectMapBaseLayers().find((l) => l.shortname === shortname);
    return layer && isBuiltInOsm(layer) ? layer : undefined;
}
/** Default upper zoom when only `minZoom` is set on a basemap layer. */
exports.DEFAULT_MAX_BASEMAP_ZOOM = 22;
/** When a basemap is out of its configured zoom range, the map shows this layer instead. */
exports.DEFAULT_FALLBACK_BASEMAP_SHORTNAME = 'osm';
function getBasemapZoomBoundsFromConfigFields(layer) {
    var _a, _b;
    if (layer.minZoom === undefined && layer.maxZoom === undefined) {
        return null;
    }
    return {
        min: (_a = layer.minZoom) !== null && _a !== void 0 ? _a : 0,
        max: (_b = layer.maxZoom) !== null && _b !== void 0 ? _b : exports.DEFAULT_MAX_BASEMAP_ZOOM
    };
}
function getZoomBoundsForBasemapShortname(shortname) {
    if (shortname === 'osm') {
        return null;
    }
    const layer = getProjectMapBaseLayers().find((l) => l.shortname === shortname);
    if (!layer || isBuiltInOsm(layer)) {
        return null;
    }
    return getBasemapZoomBoundsFromConfigFields(layer);
}
/** Zoom bounds for `shortname` when the map should fall back to OSM outside that range. */
function getZoomBoundsForOsMFallbackBasemap(shortname) {
    if (shortname === exports.DEFAULT_FALLBACK_BASEMAP_SHORTNAME) {
        return null;
    }
    return getZoomBoundsForBasemapShortname(shortname);
}
function getBasemapZoomHintMessage(shortname, currentZoom, t) {
    const bounds = getZoomBoundsForBasemapShortname(shortname);
    if (!bounds) {
        return null;
    }
    if (currentZoom >= bounds.min && currentZoom <= bounds.max) {
        return null;
    }
    if (currentZoom < bounds.min) {
        return t('main:map.controls.minZoom', { zoom: bounds.min });
    }
    return t('main:map.controls.maxZoom', { zoom: bounds.max });
}
function isStyleBasemapShortname(shortname) {
    return getProjectBasemapByShortname(shortname) !== undefined;
}
/** @deprecated Use {@link isStyleBasemapShortname}. */
exports.isVectorBasemapShortname = isStyleBasemapShortname;
/** Every basemap id the user can select (`osm` plus project `mapBaseLayers`). */
function getValidBasemapShortnames() {
    return getProjectMapBaseLayers().map((l) => l.shortname);
}
function maxZoomFromSources(sources) {
    if (!sources) {
        return undefined;
    }
    let max;
    for (const src of Object.values(sources)) {
        if (src &&
            typeof src === 'object' &&
            'maxzoom' in src &&
            typeof src.maxzoom === 'number') {
            const mz = src.maxzoom;
            max = max === undefined ? mz : Math.max(max, mz);
        }
    }
    return max;
}
/** Upper bound for `MapLibreMap` `maxZoom` from configured basemaps. */
function getMaxConfiguredRasterBasemapZoom() {
    let maxZ = exports.DEFAULT_MAX_BASEMAP_ZOOM;
    for (const layer of getProjectMapBaseLayers()) {
        if (isBuiltInOsm(layer)) {
            continue;
        }
        if ((0, mapBaseLayersProject_types_1.isProjectBasemapStyleUrl)(layer)) {
            continue;
        }
        const mz = maxZoomFromSources(layer.style.sources);
        if (mz !== undefined) {
            maxZ = Math.max(maxZ, mz);
        }
        if (layer.maxZoom !== undefined) {
            maxZ = Math.max(maxZ, layer.maxZoom);
        }
    }
    return maxZ;
}
function formatBasemapDisplayName(layer, t) {
    if (layer.shortname === 'osm' && !('name' in layer)) {
        return t('main:map.controls.osm');
    }
    if (layer.name !== undefined && layer.name !== '') {
        return layer.name;
    }
    return layer.shortname;
}
function formatRasterBasemapAttribution(layer, t) {
    if (layer.shortname === 'osm' && !('name' in layer)) {
        return t('main:map.osmAttribution');
    }
    if (layer.attribution !== undefined && layer.attribution !== '') {
        return layer.attribution;
    }
    return '';
}
//# sourceMappingURL=projectBaseMapLayers.js.map